/**
 * App chrome (SPEC §5.6).
 *
 * Portrait-only, mobile-first: one `phone-column` for every viewport. Desktop
 * gets the same column, centred — not a bespoke layout.
 *
 * The chrome carries the one thing that must be reachable from every screen:
 * the reconnect hint. Audio is on and stays on — there is no visible control
 * for it, so no screen has to yield a corner to one.
 */
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { sound } from '../lib/audio.js';
import { useDismissError, useIsReconnecting, useRoomError } from '../store/index.js';

export function Shell({ children }: { children: ReactNode }) {
  // Browsers keep the AudioContext suspended until a user gesture. The first
  // tap anywhere unlocks it, which in this game is always the join button —
  // long before the countdown chime needs to fire.
  useEffect(() => {
    const unlock = () => sound.unlock();
    const options = { passive: true } as const;
    window.addEventListener('pointerdown', unlock, options);
    window.addEventListener('keydown', unlock, options);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  return (
    <div className="phone-column relative bg-sand text-ink">
      <ConnectionBar />
      {/* `min-h-0` is what lets the bound reach the screen: without it this flex
          child refuses to shrink below its content and the column's height is
          academic. */}
      <main className="flex min-h-0 flex-1 flex-col">{children}</main>
      <ErrorToast />
    </div>
  );
}

/**
 * Reconnection is meant to be invisible (SPEC §5.4). This only appears once a
 * retry has already failed, and it never blocks anything — the last known state
 * stays on screen and is replaced wholesale by the server's snapshot. Saying so
 * is the whole point of the amber strip: nothing the player typed is lost.
 */
function ConnectionBar() {
  const reconnecting = useIsReconnecting();
  if (!reconnecting) return null;

  return (
    <div
      className="bg-amber px-4 py-2 text-center text-[0.8125rem] font-semibold text-cream"
      role="status"
      aria-live="polite"
    >
      Reconnecting… changes saved
    </div>
  );
}

/** Non-fatal server errors. Fatal ones are handled by the room route. */
function ErrorToast() {
  const error = useRoomError();
  const dismiss = useDismissError();

  useEffect(() => {
    if (!error || error.fatal) return;
    const timer = setTimeout(dismiss, 4000);
    return () => clearTimeout(timer);
  }, [error, dismiss]);

  if (!error || error.fatal) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-6"
      role="alert"
    >
      <p className="rounded-card pointer-events-auto max-w-[var(--spacing-phone)] bg-brick px-4 py-3 text-[0.8125rem] font-semibold text-cream">
        {error.message}
      </p>
    </div>
  );
}
